import { Typography } from "@/components/shared/typography";
import {
  Button,
  Card,
  CardContent,
  Image,
  Textarea,
  View,
} from "@/components/ui";
import CsatRatePerson from "@/assets/images/3d-illus/csat-rate-person.png";
import StarFeedbackImage from "@/assets/images/star-feedback.png";
import { Controller, FormProvider } from "react-hook-form";
import { useCsatRateForm, type CsatRateFormData } from "./useCsatRateForm";
import { Ratings } from "@/components/ui/ratings";
import { FormControlError } from "@/components/shared/form-control-error";
import { InputFormControl } from "@/components/shared/input-form-control";
import { collectError } from "@/lib/monitoring/sentry";
import { showToast } from "@/components/ui/toast/show-toast";
import { useState } from "react";
import { InfoDialog } from "@/components/shared/info-dialog";
import { useRoute } from "@/components/shared/router";
import { app } from "@/config/env";

const postCsatRate = async (token: string, values: CsatRateFormData) => {
  try {
    const body = {
      ticket_id: token,
      rating: values.rating,
      comment: values.comment,
    };

    const url = app.CSAT_RATE_URL;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    return true;
  } catch (error) {
    collectError(error, {
      tags: {
        feature: "csat_rate",
      },
    });
  }
};

type Props = {
  token: string;
};

export const CsatCardForm = ({ token }: Props) => {
  const csatRateForm = useCsatRateForm();
  const { formState, control, handleSubmit } = csatRateForm;

  const { replace } = useRoute();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const onSubmit = (data: CsatRateFormData) => {
    postCsatRate(token, data).then((result) => {
      if (!result) {
        showToast({
          title: "Failed to send feedback",
          type: "error",
        });
        return;
      }

      setShowSuccessModal(true);
    });
  };

  const goToHomepage = () => {
    setShowSuccessModal(false);
    replace({ pageKey: "homepage" });
  };

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-2">
        <Image src={CsatRatePerson} className="size-40" />
        <Typography variant="label-md">
          How was your experience with our support?
        </Typography>
        <Typography variant="caption-md" color="foreground-intermediate">
          Your feedback helps us serve you better.
        </Typography>
      </CardContent>
      <CardContent>
        <FormProvider {...csatRateForm}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <View className="flex flex-col gap-2">
              <Controller
                name="rating"
                control={control}
                render={({ field }) => (
                  <View className="px-14">
                    <Ratings
                      rating={field.value}
                      onClick={field.onChange}
                      disabled={field.disabled}
                      size={40}
                      variant="yellow"
                    />
                  </View>
                )}
              />
              {formState.errors.rating?.message && (
                <FormControlError error={formState.errors.rating.message} />
              )}

              <InputFormControl
                name="comment"
                className="mt-3"
                control={control}
                component={Textarea}
                componentProps={{
                  placeholder:
                    "Tell us what went well (or what we can do better)",
                }}
              />

              <Button
                variant="primary"
                color="CTA"
                rounded="full"
                fullWidth="full"
                children="Send Feedback"
              />
            </View>
          </form>
        </FormProvider>
      </CardContent>
      {showSuccessModal && (
        <InfoDialog
          icon={StarFeedback}
          open={showSuccessModal}
          onConfirm={goToHomepage}
          title="Thank you for your feedback!"
          subtitle="We truly appreciate you taking the time to share your thoughts. Your input helps us improve and serve you better."
          buttonText="Done"
        />
      )}
    </Card>
  );
};

const StarFeedback = () => {
  return <Image src={StarFeedbackImage} className="mx-auto size-[72px]" />;
};
